/*
 * @Author: Roman
 * @Date: 2022-12-12 16:37:17
 * @LastEditors: Please set LastEditors
 * @LastEditTime: 2026-07-27 11:39:58
 * @FilePath: \qpmn-site\src\app\modules\basic\page\page-object-tree.service.ts
 * @Description: 
 */
import { IError, IResponseEvent, IToken } from "@core/data-access/model";
import { IStorageService } from "@core/storage";
import { ILoginCacheKey, IPageObjectTreeService, PageCacheKey } from "."
import { IAnonymousLoginCacheKey } from "./login-cache-key";
import { ILoggerObjectTree, ILoggerUserInfo } from "@core/logger/types";
import { IUserDataService } from "@modules/user/service/data/i-user-data-service";
import { IPageInitObjectTreeService, UserPreferences } from "./page-object-tree";
import { envConfig } from "@env/env-config";
import { IHeaderObjectTreeService } from "@modules/main/header/header-object-tree";
import { IUserData } from "@modules/user/rest/data/user-data-proxy";
import { LocalStorageKeyEnum } from "@modules/main/LocalStorageKeyEnum";
import is from "@core/comparators/is";
import { Store } from "@modules/store/stores/model/domain/Store";
import { getStorage } from "@core/stdx/tool/storage";
import { appConfig } from "@env/app-config";

export abstract class PageObjectTreeService implements IPageObjectTreeService, ILoggerObjectTree, IPageInitObjectTreeService {

    public abstract storageService: IStorageService<PageCacheKey>;

    public USE_TEST_VERSION: boolean = false;

    private _UserDataService?: IUserDataService;

    private _UserPreference: UserPreferences;

    constructor() {
        if (/\/stage/.test(window.location.pathname)) {
            this.USE_TEST_VERSION = true;
        }
    }

    setCurrentPreference(preference: UserPreferences) {
        this._UserPreference = preference;
    }

    getCurrentPreference(): UserPreferences {
        // todo: 降级处理
        // 优先级：
        // 1.请求获取的偏好设置
        // 2.缓存中的偏好设置
        // 3.网站默认偏好设置
        if (!this._UserPreference) {
            let currencyCode = this.storageService.getItem(LocalStorageKeyEnum.CURRENCY_CODE) || envConfig.defaultCurrency();
            // let cartNumber = this.storageService.getItem("CartNumber") ? Number(this.storageService.getItem("CartNumber")) : 0;
            // let languageCode = this.storageService.getItem("languageCode") || appConifg.DEFAULT_LANGUAGE;

            this._UserPreference = {
                currencyCode: currencyCode,
                // cartNumber: cartNumber,
                // languageCode: languageCode
            }
        }

        return this._UserPreference;
    }

    setUserDataService(userDataService: IUserDataService) {
        this._UserDataService = userDataService;
    }

    get userPreference(): angular.IPromise<IUserData> {
        if (this._UserDataService) {
            return this._UserDataService.getUserDataWithoutCache();
        } else {
            throw new Error("未注入userDataService");
        }
    }

    get useTestVersion(): boolean {
        return this.USE_TEST_VERSION;
    }

    /**
     * @description 从localStore获取token缓存,若未登录则返回undefined
     */
    get token(): IToken | undefined {
        let storage = getStorage("cookie", appConfig.appName);
        let tokenType = storage.get<string>("token_type");
        let accessToken = storage.get<string>("access_token");

        if (accessToken == null || tokenType == null) {
            return undefined;
        } else {
            return {
                token_type: tokenType,
                access_token: accessToken,
            };
        }
    }

    getCache(key: PageCacheKey): string | null {
        if (key === LocalStorageKeyEnum.TOKEN || key === LocalStorageKeyEnum.TOKEN_TYPE) {
            let cookieStorage = getStorage("cookie", appConfig.appName);
            return cookieStorage.get<string>(key);
        } else {
            return this.storageService.getItem(key);
        }
    }

    setCache(key: PageCacheKey, value: string): void {
        if (key === LocalStorageKeyEnum.TOKEN || key === LocalStorageKeyEnum.TOKEN_TYPE) {
            let cookieStorage = getStorage("cookie", appConfig.appName);
            cookieStorage.set(key, value);
        } else {
            this.storageService.setItem(key, value);
        }
    }

    getUserName(): string {
        // 读取缓存
        let userName = this.getCache(LocalStorageKeyEnum.USER_NAME);
        return userName || "";
    }

    setLogin(loginCacheKey: ILoginCacheKey): void {
        this.setCache(LocalStorageKeyEnum.FIRST_NAME, loginCacheKey.firstName);
        this.setCache(LocalStorageKeyEnum.USER_NAME, loginCacheKey.userName);
        this.setCache(LocalStorageKeyEnum.IS_ANONYMOUS, loginCacheKey.isAnonymous.toString());
        this.setCache(LocalStorageKeyEnum.PARTNER_TYPE, loginCacheKey.partnerType);

        let storage = getStorage("cookie", appConfig.appName);
        storage.set("access_token", loginCacheKey.token);
        storage.set("token_type", loginCacheKey.tokenType);

        if (loginCacheKey.expire) {
            let currentDate = new Date().getTime();
            let expireDate = loginCacheKey.expire * 1000 + currentDate;
            this.setCache(LocalStorageKeyEnum.EXPIRE, expireDate.toString());
        }

        if (loginCacheKey.anonymousUserId) {
            this.setCache(LocalStorageKeyEnum.ANONYMOUS_USER_ID, loginCacheKey.anonymousUserId);
        }
        // 清空店铺列表缓存数据
        this.clearStoreList();
        // 清除旧格式的存储key
        this.removeOldKeyStorage();
    }

    setAnonymousLogin(loginCacheKey: IAnonymousLoginCacheKey): void {
        let storage = getStorage("cookie", appConfig.appName);
        storage.set("access_token", loginCacheKey.token);
        storage.set("token_type", loginCacheKey.tokenType);

        this.setCache(LocalStorageKeyEnum.IS_ANONYMOUS, loginCacheKey.isAnonymous.toString());

        if (loginCacheKey.expire) {
            let currentDate = new Date().getTime();
            let expireDate = loginCacheKey.expire * 1000 + currentDate;
            this.setCache(LocalStorageKeyEnum.EXPIRE, expireDate.toString());
        }

        if (loginCacheKey.anonymousUserId) {
            this.setCache(LocalStorageKeyEnum.ANONYMOUS_USER_ID, loginCacheKey.anonymousUserId);
        }
        // 清空店铺列表缓存数据
        this.clearStoreList();
        // 清除旧格式的存储key
        this.removeOldKeyStorage();
    }

    /**
     * @description 清除登录状态
     */
    public clearLogin(): void {
        this.removeCache(LocalStorageKeyEnum.USER_NAME);
        this.removeCache(LocalStorageKeyEnum.TOKEN);
        this.removeCache(LocalStorageKeyEnum.TOKEN_TYPE);
        this.removeCache(LocalStorageKeyEnum.CART_NUMBER);
        this.removeCache(LocalStorageKeyEnum.EXPIRE);
        this.removeCache(LocalStorageKeyEnum.STORE_LIST);
        this.removeCache(LocalStorageKeyEnum.STORE_LIST_EXPIRE);

        let storage = getStorage("cookie", appConfig.appName);
        storage.remove("access_token");
        storage.remove("token_type");

        // this.removeAll();
        // 清除旧key localStorage数据 
        this.removeOldKeyStorage();

        this.setCache(LocalStorageKeyEnum.IS_ANONYMOUS, "true");
    }

    removeCache(key: PageCacheKey): void {
        if (key === LocalStorageKeyEnum.TOKEN || key ===  LocalStorageKeyEnum.TOKEN_TYPE) {
            let cookieStorage = getStorage("cookie", appConfig.appName);
            cookieStorage.remove(key);
        } else {
            this.storageService.removeItem(key);
        }
    }

    removeOldKeyCache(key: string): void {
        this.storageService.removeItem(key as PageCacheKey);
    }

    /**
     * @description 获取当前/登录前的匿名用户id
     * 若从未登录则返回0
     * @returns 
     */
    public getAnonymousUserId(): number {
        // todo: 70或73环境标识
        let anonymousUserIdStorage = this.getCache(LocalStorageKeyEnum.ANONYMOUS_USER_ID);

        return anonymousUserIdStorage != null ? new Number(anonymousUserIdStorage).valueOf() : 0;
    }

    /**
     * @description 判断当前是否是匿名用户
     */
    public isAnonymous(): boolean {
        let isAnonymousStorage = this.getCache(LocalStorageKeyEnum.IS_ANONYMOUS);

        return isAnonymousStorage == null ? true : isAnonymousStorage == "true";
    }

    /**
     * @description 检查当前缓存的token是否过期(不论实名/匿名)
     * @returns 
     */
    public isExpire(): boolean {
        let currentTime = new Date().getTime();
        let expTime = this.getCache(LocalStorageKeyEnum.EXPIRE);
        // 过期时间数小于当前时间数,表示token已过期
        return Number(expTime) < currentTime;
    }

    /**
     * @description 
     * @returns 
     */
    public getPartnerType(): string | null {
        return this.getCache(LocalStorageKeyEnum.PARTNER_TYPE);
    }

    /**
     * @description 判断当前Partner是否是内部Partner
     * 是则返回true
     */
    public isInternalPartner(): boolean {
        return this.getPartnerType() == "INTERNAL";
    }

    /**
     * 日志专用的获取用户信息
     * @returns 
     */
    public getLoggerUser(): ILoggerUserInfo | null {
        let loggerUser: ILoggerUserInfo = {};

        loggerUser.userMail = this.getUserName();
        loggerUser.isAnonymous = this.isAnonymous();

        let name = this.getCache(LocalStorageKeyEnum.FIRST_NAME);

        if (name) {
            loggerUser.userName = name;
        }

        if (this.token) {
            loggerUser.tokenType = this.token.token_type;
            loggerUser.accessToken = this.token.access_token;
        }

        loggerUser.anonymousUserId = this.getAnonymousUserId();

        return loggerUser;
    }


    /**
     * @description 设置店铺列表数据
     * @param storeList
     */
    public setStoreList(storeList: Array<Store>) {
        this.setCache(LocalStorageKeyEnum.STORE_LIST, JSON.stringify(storeList));
    }

    /**
     * @description 获取店铺列表数据
     * @param storeList
     */
    public getStoreList(): Array<Store> | undefined {
        const storeListStr = this.getCache(LocalStorageKeyEnum.STORE_LIST) as string;
        return (is.undefined(storeListStr) || storeListStr === "") ? undefined : JSON.parse(storeListStr);
    }

    /**
     * @description 设置店铺列表数据-过期时间
     */
    public setStoreListExpireDate() {
        const currentDate = new Date().getTime();
        const expireDate = currentDate + (3 * 60 * 1000);
        this.setCache(LocalStorageKeyEnum.STORE_LIST_EXPIRE, String(expireDate));
    }

    /**
     * @description 获取店铺列表数据-过期时间
     * @param storeList
     */
    public getStoreListExpireDate(): number | undefined {
        const expireDate = this.getCache(LocalStorageKeyEnum.STORE_LIST_EXPIRE) as string;
        return (is.undefined(expireDate) || expireDate === "") ? undefined : Number(expireDate);
    }

    /**
     * @description 清除店铺列表缓存数据(店铺列表数据，店铺列表数据过期时间)
     */
    public clearStoreList() {
        this.removeCache(LocalStorageKeyEnum.STORE_LIST);
        this.removeCache(LocalStorageKeyEnum.STORE_LIST_EXPIRE);
    }

    public removeOldKeyStorage() {
        this.removeOldKeyCache("AnonymousUserId");
        this.removeOldKeyCache("CartNumber");
        this.removeOldKeyCache("CatalogList-Expire");
        this.removeOldKeyCache("DefaultCatalog");
        this.removeOldKeyCache("PartnerType");
        this.removeOldKeyCache("Token");
        this.removeOldKeyCache("TokenType");
        this.removeOldKeyCache("UserName");
        this.removeOldKeyCache("StoreListExpire");

        this.removeOldKeyCache("isAnonymous");
        this.removeOldKeyCache("orderStatus");
        this.removeOldKeyCache("orderStatusTime");
        this.removeOldKeyCache("firstName");
        this.removeOldKeyCache("currencyCode");
        this.removeOldKeyCache("expired");
    }

    // public removeAll() {
    //     this.storageService.removeAll();
    // }
}